import { CSSProperties } from 'react';
import { Theme } from '@mui/material';
import { TypographyOptions } from '@mui/material/styles/createTypography';
import { SeasonSansFontFamily, SeasonMixFontFamily } from '../constants';

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1: false;
    body2: false;
    inherit: false;
    subtitle1: false;
    subtitle2: false;
    overline: false;
    caption: false;
    button: false;
    h5: false;
    h6: false;
    bodyLargeSemiBold: true;
    bodyLargeRegular: true;
    bodyMediumSemiBold: true;
    bodyMediumRegular: true;
    bodySmallSemiBold: true;
    bodySmallRegular: true;
    bodyExtraSmallSemiBold: true;
    bodyExtraSmallRegular: true;
    'text-body-2xs': true;
    'text-body-2xs-bold': true;
    'text-body-3xs': true;
    'text-heading-4xl': true;
    'text-heading-3xl': true;
    'text-heading-2xl': true;
    'text-heading-lg': true;
    'text-heading-xs': true;
    'text-body-md': true;
    'text-body-sm': true;
    'text-body-xs': true;
    'text-body-xs-bold': true;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    bodyLargeSemiBold: CSSProperties;
    bodyLargeRegular: CSSProperties;
    bodyMediumSemiBold: CSSProperties;
    bodyMediumRegular: CSSProperties;
    bodySmallSemiBold: CSSProperties;
    bodySmallRegular: CSSProperties;
    bodyExtraSmallSemiBold: CSSProperties;
    bodyExtraSmallRegular: CSSProperties;
    'text-body-2xs': CSSProperties;
    'text-body-2xs-bold': CSSProperties;
    'text-body-3xs': CSSProperties;
    'text-heading-4xl': CSSProperties;
    'text-heading-3xl': CSSProperties;
    'text-heading-2xl': CSSProperties;
    'text-heading-lg': CSSProperties;
    'text-heading-xs': CSSProperties;
    'text-body-md': CSSProperties;
    'text-body-sm': CSSProperties;
    'text-body-xs': CSSProperties;
    'text-body-xs-bold': CSSProperties;
  }

  interface TypographyVariantsOptions {
    bodyLargeSemiBold?: CSSProperties;
    bodyLargeRegular?: CSSProperties;
    bodyMediumSemiBold?: CSSProperties;
    bodyMediumRegular?: CSSProperties;
    bodySmallSemiBold?: CSSProperties;
    bodySmallRegular?: CSSProperties;
    bodyExtraSmallSemiBold?: CSSProperties;
    bodyExtraSmallRegular?: CSSProperties;
    'text-body-2xs'?: CSSProperties;
    'text-body-2xs-bold'?: CSSProperties;
    'text-body-3xs'?: CSSProperties;
    'text-heading-4xl'?: CSSProperties;
    'text-heading-3xl'?: CSSProperties;
    'text-heading-2xl'?: CSSProperties;
    'text-heading-lg'?: CSSProperties;
    'text-heading-xs'?: CSSProperties;
    'text-body-md'?: CSSProperties;
    'text-body-sm'?: CSSProperties;
    'text-body-xs'?: CSSProperties;
    'text-body-xs-bold'?: CSSProperties;
  }
}

export const getTypographyOptions = ({ breakpoints, palette }: Theme) => {
  const typography: TypographyOptions = {
    fontFamily: SeasonSansFontFamily,
    h1: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: '135%',
      [breakpoints.up('mobile')]: {
        fontSize: '32px',
      },
    },
    h2: {
      fontSize: '20px',
      fontWeight: 700,
      lineHeight: '135%',
      [breakpoints.up('mobile')]: {
        fontSize: '26px',
      },
    },
    h3: {
      fontSize: '18px',
      fontWeight: 700,
      lineHeight: '135%',
      [breakpoints.up('mobile')]: {
        fontSize: '22px',
      },
    },
    h4: {
      fontSize: '16px',
      fontWeight: 700,
      lineHeight: '135%',
      [breakpoints.up('mobile')]: {
        fontSize: '18px',
      },
    },
    bodyLargeSemiBold: {
      lineHeight: '150%',
      fontWeight: 600,
      fontSize: '16px',
      [breakpoints.up('mobile')]: {
        fontSize: '18px',
      },
    },
    bodyLargeRegular: {
      lineHeight: '150%',
      fontWeight: 400,
      fontSize: '16px',
      [breakpoints.up('mobile')]: {
        fontSize: '18px',
      },
    },
    bodyMediumSemiBold: {
      lineHeight: '150%',
      fontWeight: 600,
      fontSize: '14px',
      [breakpoints.up('mobile')]: {
        fontSize: '16px',
      },
    },
    bodyMediumRegular: {
      lineHeight: '150%',
      fontWeight: 400,
      fontSize: '14px',
      [breakpoints.up('mobile')]: {
        fontSize: '16px',
      },
    },
    bodySmallSemiBold: {
      lineHeight: '150%',
      fontWeight: 600,
      fontSize: '12px',
      [breakpoints.up('mobile')]: {
        fontSize: '14px',
      },
    },
    bodySmallRegular: {
      lineHeight: '150%',
      fontWeight: 400,
      fontSize: '12px',
      [breakpoints.up('mobile')]: {
        fontSize: '14px',
      },
    },
    bodyExtraSmallSemiBold: {
      lineHeight: '150%',
      fontWeight: 600,
      fontSize: '12px',
      [breakpoints.up('mobile')]: {
        fontSize: '14px',
      },
    },
    bodyExtraSmallRegular: {
      lineHeight: '150%',
      fontWeight: 400,
      fontSize: '10px',
      [breakpoints.up('mobile')]: {
        fontSize: '12px',
      },
    },
    'text-body-2xs': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '130%',
    },
    'text-body-2xs-bold': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '130%',
    },
    'text-body-3xs': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '10px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '130%',
    },
    'text-heading-4xl': {
      fontFamily: SeasonMixFontFamily,
      fontSize: '32px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '135%',
    },
    'text-heading-3xl': {
      fontFamily: SeasonMixFontFamily,
      fontSize: '28px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '135%',
    },
    'text-heading-2xl': {
      fontFamily: SeasonMixFontFamily,
      fontSize: '24px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '135%',
    },
    'text-heading-lg': {
      fontFamily: SeasonMixFontFamily,
      fontSize: '20px',
      fontStyle: 'normal',
      fontWeight: 500,
      lineHeight: '135%',
    },
    'text-heading-xs': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 750,
      lineHeight: '135%',
    },
    'text-body-md': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '18px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '135%',
    },
    'text-body-sm': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '150%',
    },
    'text-body-xs': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '135%',
    },
    'text-body-xs-bold': {
      fontFamily: SeasonSansFontFamily,
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '135%',
    },
  };
  const typographyComponents = {
    styleOverrides: {
      root: ({ ownerState }: { ownerState: { color?: string } }) => ({
        color: ownerState.color || palette.text.primary,
        '& b, & strong': {
          fontWeight: 500,
        },
        '& span.accent, & a.accent': {
          color: palette.text.accent,
        },
        '& span.highlight, & a.highlight': {
          color: palette.text.highlight,
        },
        '& a': {
          textDecoration: 'underline',
        },
      }),
    },
    defaultProps: {
      variantMapping: {
        bodyLargeSemiBold: 'p',
        bodyLargeRegular: 'p',
        bodyMediumSemiBold: 'p',
        bodyMediumRegular: 'p',
        bodySmallSemiBold: 'p',
        bodySmallRegular: 'p',
        bodyExtraSmallSemiBold: 'p',
        bodyExtraSmallRegular: 'p',
        'text-body-2xs': 'p',
        'text-body-2xs-bold': 'p',
        'text-body-3xs': 'p',
        'text-heading-4xl': 'p',
        'text-heading-3xl': 'p',
        'text-heading-2xl': 'p',
        'text-heading-lg': 'p',
        'text-heading-xs': 'p',
        'text-body-md': 'p',
        'text-body-sm': 'p',
        'text-body-xs': 'p',
        'text-body-xs-bold': 'p',
      },
    },
  };

  return { typography, typographyComponents };
};
